import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { noop, ScrollArea, Tree, useTree } from "@mantine/core";
import { LayerListHeader } from "./LayerListHeader.js";
import { LayerListFooter } from "./LayerListFooter.js";
import { useAppAction, useAppSelector } from "../../hooks/redux.js";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { Event } from "../../constants/events.js";
import { useStyleEditor } from "../../components/StyleEditorContext.js";
import { LayerListItem } from "./LayerListItem.js";
import { LayerListGroup } from "./LayerListGroup.js";
import { IconCheck, IconChevronDown } from "@tabler/icons-react";
import { getFilteredLayersTree, getVisibleLayersIds, hideLayer, selectLayer, showLayer, } from "../../reducers/layersReducer.js";
import { PrimaryPanel } from "../../components/SidePanel.js";
import { memoize } from "es-toolkit";
import { normalizeToArray } from "../../utils/dash.js";
function renderNodeImpl({ payload, handlers }) {
    const { level, node: { value, label, nodeProps, children }, expanded, selected, tree: { isNodeChecked }, } = payload;
    const indent = level * 8;
    const checked = isNodeChecked(value);
    const handleCheckboxChange = (event) => {
        event.stopPropagation();
        handlers.current?.onCheckboxChange(value);
    };
    const handleSelectItem = (event) => {
        event.stopPropagation();
        handlers.current?.onSelectLayer(value);
    };
    const handleExpandGroup = (event) => {
        event.stopPropagation();
        handlers.current?.onExpandGroup(value);
    };
    if (children) {
        const { icon, parent, childGroups } = nodeProps;
        return (_jsx(LayerListGroup, { id: value, icon: icon, parent: parent, label: label, indent: indent, childGroups: childGroups, expanded: expanded, onClick: handleExpandGroup, rightControl: (_jsx(IconChevronDown, { size: 18, style: { transform: expanded ? "rotate(180deg)" : "rotate(0deg)" } })) }, value));
    }
    else {
        const { parent, layer: { type, id }, } = nodeProps;
        const opacity = checked ? 1 : 0.2;
        return (_jsx(LayerListItem, { type: type, id: id, parent: parent, selected: selected, indent: indent, grouped: true, rightControl: (_jsx(IconCheck, { size: 14, cursor: "pointer", opacity: opacity, onClick: handleCheckboxChange })), onClick: handleSelectItem }, id));
    }
}
function getCacheKey({ payload: { node, level, expanded, selected, tree } }) {
    return JSON.stringify({
        level,
        node,
        expanded,
        selected,
        checked: tree.isNodeChecked(node.value),
    });
}
export const LayerListGrouped = memo(({ selected, onClose }) => {
    const { captureEvent } = useStyleEditor();
    const select = useAppAction(selectLayer);
    const hide = useAppAction(hideLayer);
    const show = useAppAction(showLayer);
    const handlers = useRef(null);
    const layersTree = useAppSelector(getFilteredLayersTree);
    const visible = useAppSelector(getVisibleLayersIds);
    const memoizedRenderNode = useMemo(() => memoize(renderNodeImpl, { getCacheKey }), []);
    useEffect(() => {
        return () => {
            memoizedRenderNode.cache.clear();
        };
    }, []);
    const initialSelectedState = useMemo(() => {
        return normalizeToArray(selected?.id);
    }, [selected]);
    const tree = useTree({
        multiple: false,
        initialSelectedState,
        initialCheckedState: visible,
    });
    const { checkNode, uncheckNode, isNodeChecked, toggleSelected, toggleExpanded } = tree;
    const onExpandGroup = useCallback((id) => {
        captureEvent(Event.ExpandLayerGroup, {
            id,
        });
        toggleExpanded(id);
    }, [captureEvent, toggleExpanded]);
    const onSelectLayer = useCallback((id) => {
        captureEvent(Event.SelectLayer, {
            id,
        });
        select(id);
        toggleSelected(id);
    }, [captureEvent, toggleSelected, select]);
    const onCheckboxChange = useCallback((id) => {
        if (isNodeChecked(id)) {
            captureEvent(Event.HideLayer, {
                id,
            });
            hide(id);
            uncheckNode(id);
        }
        else {
            captureEvent(Event.ShowLayer, {
                id,
            });
            show(id);
            checkNode(id);
        }
    }, [checkNode, uncheckNode, isNodeChecked, captureEvent, hide, show]);
    handlers.current = {
        onExpandGroup,
        onSelectLayer,
        onCheckboxChange,
    };
    const handleRenderNode = useCallback((payload) => {
        return memoizedRenderNode({
            payload,
            handlers,
        });
    }, [memoizedRenderNode]);
    /**
     * Workaround for:
     * @see https://github.com/mantinedev/mantine/issues/7266
     */
    const treeWorkaround = useMemo(() => {
        return {
            ...tree,
            setFocusedNode: noop,
            setHoveredNode: noop,
        };
    }, [tree]);
    return (_jsxs(PrimaryPanel, { children: [_jsx(LayerListHeader, { onClose: onClose }), _jsx(ScrollArea, { flex: "1", offsetScrollbars: true, mr: -8, children: _jsx(Tree, { tree: treeWorkaround, data: layersTree, renderNode: handleRenderNode }) }), _jsx(LayerListFooter, {})] }));
});
LayerListGrouped.displayName = "LayerListGrouped";
